// apps/web/src/components/Footer.tsx

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Truck,
  Twitter,
  Facebook,
  Linkedin,
  Sparkles,
  Package,
  Users,
  TrendingUp,
  Activity,
  MapPin,
  ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from '@/components/ui/separator';
import TextReveal from '@/components/ui/TextReveal';
import { useTheme } from "next-themes";

// ═══════════════════════════════════════════════════════════════════════════
// QUICK QUOTE FORM (Restyled)
// ═══════════════════════════════════════════════════════════════════════════
const QuickQuoteForm = () => {
  const [pickup, setPickup] = useState('');
  const [delivery, setDelivery] = useState('');
  const [tonnage, setTonnage] = useState<number>(1);
  const [quote, setQuote] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    if (!pickup || !delivery || pickup === delivery || tonnage <= 0) {
      setQuote(null);
      return;
    }

    setIsLoading(true);
    setQuote(null);

    // Simulate API call with distance calculation
    setTimeout(() => {
      // Approximate distances between major cities in Malawi (in km)
      const distances: Record<string, Record<string, number>> = {
        'Blantyre': { 'Lilongwe': 320, 'Mzuzu': 550, 'Zomba': 70, 'Mangochi': 150 },
        'Lilongwe': { 'Blantyre': 320, 'Mzuzu': 370, 'Zomba': 250, 'Mangochi': 220 },
        'Mzuzu': { 'Blantyre': 550, 'Lilongwe': 370, 'Zomba': 480, 'Mangochi': 400 },
        'Zomba': { 'Blantyre': 70, 'Lilongwe': 250, 'Mzuzu': 480, 'Mangochi': 80 },
        'Mangochi': { 'Blantyre': 150, 'Lilongwe': 220, 'Mzuzu': 400, 'Zomba': 80 }
      };

      const distance = distances[pickup]?.[delivery] || 200; // Default 200km if not found

      // Formula: MWK 250 × tonnage × distance (km)
      const finalQuote = 250 * tonnage * distance;

      setQuote(finalQuote);
      setIsLoading(false);
    }, 1500);
  };

  return (
    <form onSubmit={handleCalculate} className="space-y-4 p-8 bg-foreground/5 backdrop-blur-md rounded-3xl border border-foreground/10 relative overflow-hidden group hover:bg-foreground/10 transition-colors duration-500">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles size={20} className="text-primary flex-shrink-0" />
          <h3 className="font-bold text-xl text-foreground">Instant Quote</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          AI-powered estimation for standard cargo.
        </p>

        <div className="space-y-3">
          <Select onValueChange={setPickup} value={pickup}>
            <SelectTrigger className="bg-background/50 border-foreground/10 text-foreground placeholder:text-muted-foreground h-12 rounded-xl focus:ring-primary/50">
              <SelectValue placeholder="Pickup City" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Blantyre">Blantyre</SelectItem>
              <SelectItem value="Lilongwe">Lilongwe</SelectItem>
              <SelectItem value="Mzuzu">Mzuzu</SelectItem>
              <SelectItem value="Zomba">Zomba</SelectItem>
              <SelectItem value="Mangochi">Mangochi</SelectItem>
            </SelectContent>
          </Select>

          <Select onValueChange={setDelivery} value={delivery}>
            <SelectTrigger className="bg-background/50 border-foreground/10 text-foreground placeholder:text-muted-foreground h-12 rounded-xl focus:ring-primary/50">
              <SelectValue placeholder="Delivery City" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Mzuzu">Mzuzu</SelectItem>
              <SelectItem value="Lilongwe">Lilongwe</SelectItem>
              <SelectItem value="Blantyre">Blantyre</SelectItem>
              <SelectItem value="Zomba">Zomba</SelectItem>
              <SelectItem value="Mangochi">Mangochi</SelectItem>
            </SelectContent>
          </Select>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Tonnage (tons)</label>
            <input
              type="number"
              min="0.1"
              step="0.1"
              value={tonnage}
              onChange={(e) => setTonnage(parseFloat(e.target.value) || 1)}
              className="w-full bg-background/50 border border-foreground/10 text-foreground placeholder:text-muted-foreground h-12 rounded-xl px-4 focus:ring-2 focus:ring-primary/50 focus:outline-none"
              placeholder="Enter tonnage"
            />
          </div>
        </div>

        <Button
          type="submit"
          className="w-full mt-6 bg-foreground text-background hover:bg-foreground/90 font-bold h-12 rounded-xl"
          disabled={isLoading || !pickup || !delivery}
        >
          {isLoading ? (
            <>
              <Activity className="w-4 h-4 mr-2 animate-spin" />
              Calculating...
            </>
          ) : (
            <>
              Get Estimate
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>

        <AnimatePresence>
          {quote && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="pt-4 border-t border-foreground/10 mt-4"
            >
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-xs text-muted-foreground">Estimated Cost</p>
                  <p className="text-2xl font-black text-foreground">
                    MWK {quote.toLocaleString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-primary font-medium flex items-center justify-end">
                    <MapPin className="w-3 h-3 mr-1" />
                    {pickup} → {delivery}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </form>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// FOOTER LINK COLUMN
// ═══════════════════════════════════════════════════════════════════════════
interface FooterLink {
  href: string;
  label: string;
}

const FooterLinkColumn = ({ title, links }: { title: string; links: FooterLink[] }) => (
  <div className="space-y-4">
    <h3 className="font-bold text-foreground text-lg tracking-tight">{title}</h3>
    <ul className="space-y-3">
      {links.map((link) => (
        <li key={link.href}>
          <Link
            to={link.href}
            className="text-muted-foreground hover:text-primary transition-all duration-300 text-sm flex items-center group"
          >
            <span className="w-0 group-hover:w-2 transition-all duration-300 h-[1px] bg-primary mr-0 group-hover:mr-2" />
            {link.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// MAIN FOOTER COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export const Footer = () => {
  const { resolvedTheme } = useTheme();
  const currentYear = new Date().getFullYear();

  const clientLinks: FooterLink[] = [
    { href: "/jobs/create", label: "Post a Job" },
    { href: "/dashboard", label: "My Dashboard" },
    { href: "/how-it-works", label: "How It Works" },
    { href: "/faq", label: "FAQ for Clients" },
  ];

  const transporterLinks: FooterLink[] = [
    { href: "/jobs", label: "Find Jobs" },
    { href: "/dashboard/my-vehicles", label: "Manage Vehicles" },
    { href: "/register", label: "Become a Partner" },
    { href: "/faq", label: "FAQ for Transporters" },
  ];

  const fleetLinks: FooterLink[] = [
    { href: "/register?role=FLEET_MANAGER", label: "Register Fleet" },
    { href: "/register/driver", label: "Join as Driver" },
    { href: "/how-it-works", label: "Fleet Operations" },
    { href: "/faq", label: "FAQ for Fleets" },
  ];

  const companyLinks: FooterLink[] = [
    { href: "/about-us", label: "About Us" },
    { href: "/contact", label: "Contact Support" },
    { href: "/terms-of-service", label: "Terms of Service" },
    { href: "/privacy-policy", label: "Privacy Policy" },
    { href: "/operations", label: "All Operations" },
  ];

  return (
    <footer className="relative bg-background text-foreground pt-32 pb-12 overflow-hidden z-40 transition-colors duration-500 border-t border-foreground/5">

      {/* Cinematic Background Text (Parallax feel) */}
      <div className="absolute bottom-[-5%] left-0 w-full overflow-hidden leading-none select-none pointer-events-none opacity-[0.03]">
        <h1 className="text-[25vw] font-black uppercase text-center whitespace-nowrap tracking-tighter text-foreground">
          Logistics
        </h1>
      </div>

      <div className="container mx-auto px-4 relative z-10">

        {/* Top Section: CTA & Quote */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24 items-center">
          <div className="space-y-8">
            <TextReveal className="text-6xl md:text-8xl font-black leading-[0.9] tracking-tighter text-foreground">
              Ready. Set. Go.
            </TextReveal>
            <p className="text-xl text-muted-foreground max-w-md font-light leading-relaxed">
              Join the neural network of modern supply chains. Whether you send or move, we verify, track, and secure it.
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <Button asChild size="xl" className="text-lg px-8 py-6 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all font-bold">
                <Link to="/register">Start Now</Link>
              </Button>
              <Button asChild size="xl" variant="outline" className="text-lg px-8 py-6 rounded-full border-foreground/20 hover:bg-foreground/5 text-foreground transition-all">
                <Link to="/login">Login</Link>
              </Button>
            </div>
          </div>

          <div className="lg:pl-12">
            <QuickQuoteForm />
          </div>
        </div>

        <Separator className="my-16 bg-foreground/10" />

        {/* Links Grid - Now with 5 columns */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 md:gap-12 mb-20">
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <Link to="/" className="flex items-center space-x-2 mb-6 group">
              <img
                src={resolvedTheme === 'dark'
                  ? "https://res.cloudinary.com/unihousingmw/image/upload/v1765271781/favicon-32x32_vrwdzo.png"
                  : "https://res.cloudinary.com/unihousingmw/image/upload/v1765271781/transparent-favicon_ezmbmf.png"
                }
                alt="K&C Logistics"
                className="w-12 h-12 object-contain group-hover:scale-110 transition-transform duration-300"
              />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed">
              K&C Logistics is a technology platform connecting shippers and carriers with military-grade precision and speed.
            </p>
          </div>

          <FooterLinkColumn title="For Clients" links={clientLinks} />
          <FooterLinkColumn title="For Transporters" links={transporterLinks} />
          <FooterLinkColumn title="For Fleets" links={fleetLinks} />
          <FooterLinkColumn title="Company" links={companyLinks} />
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center text-xs text-muted-foreground gap-6 pt-8 border-t border-foreground/5">
          <div className="flex items-center gap-6">
            <p>&copy; {currentYear} K & C Logistics Inc. <span className="mx-2 text-primary/50">|</span> Powered by <span className="font-bold text-primary">CODE1407</span></p>
            <Link
              to="/status"
              className="flex items-center gap-2 hover:text-green-500 transition-colors"
            >
              <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              Systems Online
            </Link>
          </div>

          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-foreground/20" />
              <span>1.2k+ Deliveries</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-foreground/20" />
              <span>300+ Partners</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-foreground/20" />
              <span>98% Accuracy</span>
            </div>
          </div>

          <div className="flex space-x-6">
            {[Twitter, Facebook, Linkedin].map((Icon, i) => (
              <a key={i} href="#" className="hover:text-primary transition-colors">
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};